'use client';
import * as styles from './styles.css';
export { styles as dateInputStyles };
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useState, useRef, useLayoutEffect, forwardRef, useContext, useMemo, useCallback, useEffect } from 'react';
import { CalendarIcon, ClockSmIcon, ArrowRightIcon, ArrowLeftIcon } from '@paypalcorp/pp-react-icons';
import { MaskType, MaskedTextInput } from '@paypalcorp/pp-react-masked-text-input';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { Sheet } from '@paypalcorp/pp-react-sheet';
import { hasWindow, KeyboardCode, cx, devWarning } from '@paypalcorp/pp-react-utils';
import { IntlContext } from '@paypalcorp/pp-react-intl';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { CaptionText, BodyText } from '@paypalcorp/pp-react-text';
import { IconButton } from '@paypalcorp/pp-react-icon-button';

var DEFAULT_MASK_OPTIONS = {
  mask: 'm`/d`/y',
  // here backquote ` is needed to prevent characters shifting back on edit. Refer https://github.com/uNmAnNeR/imaskjs/issues/89
  lazy: false,
  blocks: {
    d: {
      mask: MaskType.Range,
      from: 1,
      to: 31,
      maxLength: 2,
      placeholderChar: 'D'
    },
    m: {
      mask: MaskType.Range,
      from: 1,
      to: 12,
      maxLength: 2,
      placeholderChar: 'M'
    },
    y: {
      mask: MaskType.Range,
      from: 1900,
      to: 2999,
      maxLength: 4,
      placeholderChar: 'Y'
    }
  }
};
var MD_BREAKPOINT = 752;
var BASE_FONT_SIZE = 16;
var BASE_CALENDAR_IDEAL_WIDTH = 200;
var CalendarRange = /*#__PURE__*/function (CalendarRange) {
  CalendarRange["START"] = "start";
  CalendarRange["END"] = "end";
  return CalendarRange;
}(CalendarRange || {});
var MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
var DAY_NAMES = {
  sun: 'Sun',
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat'
};

var CalendarContainer = function (_ref) {
  var isCalendarVisible = _ref.isCalendarVisible,
    label = _ref.label,
    children = _ref.children,
    calendarTriggerRef = _ref.calendarTriggerRef,
    wrapperContainerRef = _ref.wrapperContainerRef,
    onShowCalendar = _ref.onShowCalendar,
    _ref$calendarInSheet = _ref.calendarInSheet,
    calendarInSheet = _ref$calendarInSheet === void 0 ? false : _ref$calendarInSheet;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isMobile = _useState2[0],
    setMobile = _useState2[1];
  var containerRef = useRef();
  var _useState3 = useState(styles.calendarContainer),
    _useState4 = _slicedToArray(_useState3, 2),
    calendarPositionStyles = _useState4[0],
    setCalendarPositionStyles = _useState4[1];
  var isMobileViewPort = function () {
    return /* istanbul ignore next */(
      hasWindow() && window.innerWidth < MD_BREAKPOINT
    );
  };

  /* istanbul ignore next */
  var handleKey = function (evt) {
    if (new KeyboardCode(evt.key || evt.keyCode).IS_ESC) {
      onShowCalendar(false);
    }
  };

  /* istanbul ignore next */
  var handleOutsideClick = function (evt) {
    if (!calendarTriggerRef.current.contains(evt.target) && !containerRef.current.contains(evt.target)) {
      onShowCalendar(false);
    }
  };

  /* istanbul ignore next */
  var handleResize = function () {
    /* Event to dismiss calendar popup on window resize */
    onShowCalendar(false);
  };
  var getRefCurrent = function (myref) {
    return /* istanbul ignore next */(
      myref && myref.current ? myref.current : null
    );
  };

  /* istanbul ignore next */
  var getCalendarBoundary = function () {
    var calendarTrigger = getRefCurrent(calendarTriggerRef);
    var calendarTriggerPos = calendarTrigger.getBoundingClientRect();
    var windowWidth = hasWindow() && window.innerWidth || document.documentElement.clientWidth;
    var fontSizeVal = hasWindow() && parseFloat(window.getComputedStyle(document.documentElement).fontSize);
    var calculatedSpace = BASE_CALENDAR_IDEAL_WIDTH + BASE_CALENDAR_IDEAL_WIDTH * ((fontSizeVal - BASE_FONT_SIZE) * 0.0625);
    if (wrapperContainerRef === null || wrapperContainerRef === void 0 ? void 0 : wrapperContainerRef.current) {
      var wrapperContainerPos = wrapperContainerRef.current.getBoundingClientRect();
      if (calendarTriggerPos.left - wrapperContainerPos.left < calculatedSpace) {
        // Left side is out of viewoprt
        setCalendarPositionStyles(styles.calendarContainerRight);
      } else if (wrapperContainerPos.right + calculatedSpace > wrapperContainerPos.width) {
        // Right side is out of viewport
        setCalendarPositionStyles(styles.calendarContainerLeft);
      }
    } else if (calendarTrigger) {
      if (calendarTriggerPos.left < calculatedSpace) {
        // Left side is out of viewoprt
        setCalendarPositionStyles(styles.calendarContainerRight);
      } else if (calendarTriggerPos.right + calculatedSpace > windowWidth) {
        // Right side is out of viewport
        setCalendarPositionStyles(styles.calendarContainerLeft);
      }
    }
  };
  useLayoutEffect(function () {
    setCalendarPositionStyles(styles.calendarContainer);
    var _isMobile = isMobileViewPort();
    getCalendarBoundary();
    setMobile(_isMobile);
    if (!_isMobile && isCalendarVisible) {
      var _containerRef$current, _containerRef$current2;
      window.addEventListener('resize', handleResize);
      document.addEventListener('keydown', handleKey);

      // Add handleOutSideClick when calendar is not in sheet, let sheet handle outside click on its own
      /* istanbul ignore next */
      if (!calendarInSheet) {
        document.addEventListener('click', handleOutsideClick);
      }

      // eslint-disable-next-line no-unused-expressions
      (_containerRef$current = containerRef.current) === null || _containerRef$current === void 0 ? void 0 : (_containerRef$current2 = _containerRef$current.scrollIntoView) === null || _containerRef$current2 === void 0 ? void 0 : _containerRef$current2.call(_containerRef$current, {
        behavior: 'smooth',
        block: 'nearest'
      });
    }
    return function () {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isCalendarVisible]);
  if (isMobile || calendarInSheet) {
    /* istanbul ignore next */
    return /*#__PURE__*/React.createElement(Sheet, {
      isOpen: isCalendarVisible,
      onCloseComplete: function onCloseComplete() {
        return onShowCalendar(false);
      },
      noCloseButton: true,
      title: label,
      skipFormFocus: true,
      "data-ppui": "true"
    }, calendarInSheet && !isMobile ? /*#__PURE__*/React.createElement("div", {
      className: styles.tableFullWidth,
      "data-ppui": "true"
    }, children) : children);
  }
  return isCalendarVisible ? /*#__PURE__*/React.createElement("div", {
    className: calendarPositionStyles,
    ref: containerRef,
    "data-ppui": "true"
  }, children) : null;
};

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function defaultParseDate(str) {
  /* istanbul ignore next */
  if (!str) {
    return null;
  }
  var parts = str.split('/');
  var month = Number(parts[0]);
  var date = Number(parts[1]);
  var year = Number(parts[2]);
  return new Date(year, month - 1, date);
}
function defaultFormatDate(date) {
  if (!date) {
    return '';
  }
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  }).format(date);
}
var localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

// To get the placeholderChars in a separate variable, e.g., {d: 'D', m: 'M', y: 'Y'}, to be used when updating the masked options.
function getLocalePlaceholderChars(maskPlaceholderData) {
  return maskPlaceholderData.reduce(function (placeholderChars, _ref) {
    var type = _ref.type,
      value = _ref.value;
    if (type === 'month' || type === 'day' || type === 'year') {
      placeholderChars[type] = value[0].toUpperCase();
    }
    return placeholderChars;
  }, {
    day: '',
    month: '',
    year: ''
  });
}
function getDateMaskOptions(intlContext, formatterSkeleton, maskOptions) {
  if (!(intlContext && intlContext.dateInputMaskFormat)) {
    return _objectSpread(_objectSpread({}, DEFAULT_MASK_OPTIONS), maskOptions);
  }
  // Get the order of the date fields and the placeholder
  var _intlContext$dateInpu = intlContext.dateInputMaskFormat({
      style: formatterSkeleton
    }),
    maskArray = _intlContext$dateInpu.maskArray;
  var localePlaceholderChars = getLocalePlaceholderChars(maskArray);
  var pattern = maskArray.map(function (_ref2) {
    var type = _ref2.type,
      value = _ref2.value;
    switch (type) {
      case 'month':
        return 'm';
      case 'day':
        return 'd';
      case 'year':
        return 'y';
      default:
        // Remove any rtl marker chars
        // eslint-disable-next-line
        var separator = value.replace(/[^\x00-\x7F]/g, '');
        return "`".concat(separator);
    }
  }).join('');

  // To update the maskOption by merging the defaultMaskOptions (which is constant), the maskOptions (which we are receiving as a prop),placeholder characters and the mask (which we are getting from worldReady, different for each locale).
  return _objectSpread(_objectSpread(_objectSpread({}, DEFAULT_MASK_OPTIONS), {}, {
    blocks: {
      d: _objectSpread(_objectSpread({}, DEFAULT_MASK_OPTIONS.blocks.d), {}, {
        placeholderChar: localePlaceholderChars.day
      }),
      m: _objectSpread(_objectSpread({}, DEFAULT_MASK_OPTIONS.blocks.m), {}, {
        placeholderChar: localePlaceholderChars.month
      }),
      y: _objectSpread(_objectSpread({}, DEFAULT_MASK_OPTIONS.blocks.y), {}, {
        placeholderChar: localePlaceholderChars.year
      })
    }
  }, maskOptions), {}, {
    mask: pattern
  });
}
function getFormatters(intlContext, formatterSkeleton, formatDate, parseDate) {
  if (!(intlContext && intlContext.dateTimeFormatter)) {
    return {
      _formatDate: formatDate || defaultFormatDate,
      _parseDate: parseDate || defaultParseDate
    };
  }
  // Get formatters from Worldready
  var dateTimeFormatter = intlContext.dateTimeFormatter({
    style: formatterSkeleton,
    timeZone: localTimeZone
  });
  return {
    _formatDate: formatDate || function _formatDate(date) {
      /* istanbul ignore next */
      if (!date) {
        return '';
      }
      return dateTimeFormatter.format(date);
    },
    _parseDate: parseDate || function _parseDate(str) {
      return str && dateTimeFormatter.parse(str);
    }
  };
}
function getFormattedDateFromParts(parts) {
  return parts.reduce(function (acc, _ref3) {
    var type = _ref3.type,
      value = _ref3.value;
    if (['month', 'day', 'year'].includes(type)) {
      acc[type] = value;
    }
    return acc;
  }, {});
}
function defaultFormatToParts(date) {
  var parts = new Intl.DateTimeFormat('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  }).formatToParts(date);
  return getFormattedDateFromParts(parts);
}
function isDateInRange(date, selectedDate, range, rangeCompletionDate) {
  if (range === CalendarRange.START && selectedDate && rangeCompletionDate) {
    return date.getTime() >= selectedDate.getTime() && date.getTime() <= rangeCompletionDate.getTime();
  } else if (range === CalendarRange.END && selectedDate && rangeCompletionDate) {
    return date.getTime() <= selectedDate.getTime() && date.getTime() >= rangeCompletionDate.getTime();
  }
  return false;
}
function isDateEqual(date, providedDate) {
  return providedDate && date.getTime() === providedDate.getTime();
}
function isDateRangeStart(date, selectedDate, range, rangeCompletionDate) {
  return range === CalendarRange.START && selectedDate && date.getTime() === selectedDate.getTime() || range === CalendarRange.END && rangeCompletionDate && date.getTime() === rangeCompletionDate.getTime();
}
function isDateRangeEnd(date, selectedDate, range, rangeCompletionDate) {
  return range === CalendarRange.END && selectedDate && date.getTime() === selectedDate.getTime() || range === CalendarRange.START && rangeCompletionDate && date.getTime() === rangeCompletionDate.getTime();
}
function getDatesForMonth(date, daysOfWeek, firstDayOfWeek, selectedDate, range, rangeCompletionDate, dueDate, isDateDisabled, calendarRange) {
  var dates = [];
  var startMonth = date.getMonth();
  var startYear = date.getFullYear();
  var from = new Date(startYear, startMonth, 1);
  var firstday = Object.keys(DAY_NAMES)[from.getDay()];
  var firstDayIndex = daysOfWeek.indexOf(firstday);
  var firstDOWIndex = Object.keys(DAY_NAMES).indexOf(firstDayOfWeek);
  var todayDate = new Date();
  var today = new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate());
  from.setDate(-firstDayIndex + 1);
  while (from) {
    var month = from.getMonth();
    var day = from.getDate();
    var year = from.getFullYear();
    if (year === startYear && month > startMonth || year > startYear) {
      if (from.getDay() === firstDOWIndex) break;
    }
    var dateObj = new Date(year, month, day);
    var isDisabled = isDateDisabled && isDateDisabled(dateObj);
    if (calendarRange === CalendarRange.START && (month < startMonth || year < startYear)) {
      isDisabled = true;
    }
    if (calendarRange === CalendarRange.END && (month > startMonth || year > startYear)) {
      isDisabled = true;
    }
    dates.push({
      day: day,
      date: dateObj,
      isDisabled: isDisabled,
      isToday: isDateEqual(from, today),
      inCurrentMonth: month === startMonth,
      isSelected: isDateEqual(from, selectedDate),
      isDue: isDateEqual(from, dueDate),
      inRange: isDateInRange(from, selectedDate, range, rangeCompletionDate),
      isRangeStart: isDateRangeStart(from, selectedDate, range, rangeCompletionDate),
      isRangeEnd: isDateRangeEnd(from, selectedDate, range, rangeCompletionDate)
    });
    from.setDate(day + 1);
  }
  return dates;
}
function getDaysOfWeek(firstDayOfWeek) {
  var daysOfWeek = Object.keys(DAY_NAMES);
  var firstDOWIndex = daysOfWeek.indexOf(firstDayOfWeek);
  if (firstDOWIndex === 0) {
    return daysOfWeek;
  }
  return daysOfWeek.slice(firstDOWIndex).concat(daysOfWeek.slice(0, firstDOWIndex));
}
function getFirstDayOfWeek(intlContext, firstDayOfWeek) {
  if (intlContext && intlContext.worldReadyObj && intlContext.worldReadyObj.weekInfo) {
    return firstDayOfWeek || intlContext.worldReadyObj.weekInfo.firstDay;
  }
  return firstDayOfWeek || 'sun';
}
function getNames(intlContext, monthNames, dayNames) {
  if (intlContext && intlContext.getDisplayNamesObject) {
    var monthDisplayNames = intlContext.getDisplayNamesObject({
      type: 'month',
      style: 'wide'
    });
    var _monthNames = monthDisplayNames.select(['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12']);
    var dayDisplayNames = intlContext.getDisplayNamesObject({
      type: 'day',
      style: 'abbreviated'
    });
    var keys = Object.keys(DAY_NAMES);
    var values = dayDisplayNames.select(keys);
    var _dayNames = keys.reduce(function (acc, key, index) {
      acc[key] = values[index];
      return acc;
    }, {});
    return {
      _monthNames: monthNames || _monthNames,
      _dayNames: dayNames || _dayNames
    };
  }
  return {
    _monthNames: monthNames || MONTH_NAMES,
    _dayNames: dayNames || DAY_NAMES
  };
}
function getFormatter(intlContext, formatToParts, formatterSkeleton) {
  if (intlContext && intlContext.dateTimeFormatter) {
    var dateTimeFormatter = intlContext.dateTimeFormatter({
      style: formatterSkeleton,
      // eslint-disable-next-line
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    var fn = function (date) {
      var parts = dateTimeFormatter.formatToParts(date);
      return getFormattedDateFromParts(parts);
    };
    return formatToParts || fn;
  }
  return formatToParts || defaultFormatToParts;
}
function areMonthsEqual(date1, date2) {
  return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth();
}

var _excluded = ["name", "label", "className", "calendar", "isCalendarVisible", "wrapperContainerRef", "onShowCalendar", "maskOptions", "selectedDate", "onSelection", "parseDate", "formatDate", "formatterSkeleton", "sheetContainerRef", "onComplete", "onAccept", "calendarInSheet", "isLabelHidden"];
var DateInput = forwardRef(function (_ref, ref) {
  var name = _ref.name,
    label = _ref.label,
    className = _ref.className,
    calendar = _ref.calendar,
    isCalendarVisible = _ref.isCalendarVisible,
    wrapperContainerRef = _ref.wrapperContainerRef,
    onShowCalendar = _ref.onShowCalendar,
    maskOptions = _ref.maskOptions,
    selectedDate = _ref.selectedDate,
    onSelection = _ref.onSelection,
    parseDate = _ref.parseDate,
    formatDate = _ref.formatDate,
    _ref$formatterSkeleto = _ref.formatterSkeleton,
    formatterSkeleton = _ref$formatterSkeleto === void 0 ? 'yyyyMMdd' : _ref$formatterSkeleto,
    sheetContainerRef = _ref.sheetContainerRef,
    onComplete = _ref.onComplete,
    onAccept = _ref.onAccept,
    calendarInSheet = _ref.calendarInSheet,
    isLabelHidden = _ref.isLabelHidden,
    rest = _objectWithoutProperties(_ref, _excluded);
  if (!onSelection) {
    onSelection = function () {
      return devWarning('Required prop `onSelection` is missing');
    };
  }
  var intlContext = useContext(IntlContext);
  var calendarTriggerRef = useRef();
  var internalRef = useRef();
  var inputRef = ref || internalRef;
  var prevVal = useRef();
  var _maskOptions = useMemo(function () {
    return getDateMaskOptions(intlContext, formatterSkeleton, maskOptions);
  }, [formatterSkeleton, maskOptions]);
  var _useMemo = useMemo(function () {
      return getFormatters(intlContext, formatterSkeleton, formatDate, parseDate);
    }, [formatDate, parseDate]),
    _formatDate = _useMemo._formatDate,
    _parseDate = _useMemo._parseDate;
  var handleComplete = function (value, unmaskedValue, typedValue) {
    /* istanbul ignore next */
    if (unmaskedValue !== prevVal.current) {
      onSelection(_parseDate(value));
    }
    if (onComplete) {
      onComplete(value, unmaskedValue, typedValue);
    }
  };
  var handleAccept = function (value, unmaskedValue, typedValue) {
    if (!unmaskedValue) {
      onSelection(null);
    }
    if (onAccept) {
      onAccept(value, unmaskedValue, typedValue);
    }
  };
  var icon = calendar && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CalendarIcon, {
    size: "sm",
    onMouseDown: function onMouseDown(evt) {
      return evt.preventDefault();
    },
    onClick: function handleIconClick() {
      var inputElm = inputRef.current;
      inputElm.focus();
      onShowCalendar(!isCalendarVisible);
    },
    svgElementRef: calendarTriggerRef,
    color: "sysColorBackgroundMediumContrast",
    className: styles.calendarTrigger,
    "aria-hidden": "true",
    "data-ppui": "true"
  }), /*#__PURE__*/React.createElement(CalendarContainer, {
    isCalendarVisible: isCalendarVisible,
    onShowCalendar: onShowCalendar,
    label: label,
    sheetContainerRef: sheetContainerRef,
    wrapperContainerRef: wrapperContainerRef,
    calendarTriggerRef: calendarTriggerRef,
    calendarInSheet: calendarInSheet,
    "data-ppui": "true"
  }, calendar));
  var unmaskedValue = _formatDate(selectedDate).replace(/\D/g, '');
  prevVal.current = unmaskedValue;
  var wrapperClassNames = cx(styles.dateInputWrap, className);
  return /*#__PURE__*/React.createElement(MaskedTextInput, _extends({
    name: name,
    label: label,
    rightIcon: icon,
    maskOptions: _maskOptions,
    className: wrapperClassNames,
    inputMode: "numeric",
    "data-ppui-info": "date-input_4.1.6__8.12.2-beta.0"
  }, rest, {
    onComplete: handleComplete,
    onAccept: handleAccept,
    value: unmaskedValue,
    ref: inputRef,
    isLabelHidden: isLabelHidden
  }));
});

/* eslint-disable complexity */
var Calendar = function (_ref) {
  var className = _ref.className,
    monthNames = _ref.monthNames,
    dayNames = _ref.dayNames,
    firstDayOfWeek = _ref.firstDayOfWeek,
    _ref$prevMonthLabel = _ref.prevMonthLabel,
    prevMonthLabel = _ref$prevMonthLabel === void 0 ? 'Go to prev month' : _ref$prevMonthLabel,
    _ref$nextMonthLabel = _ref.nextMonthLabel,
    nextMonthLabel = _ref$nextMonthLabel === void 0 ? 'Go to next month' : _ref$nextMonthLabel,
    selectedDate = _ref.selectedDate,
    range = _ref.range,
    rangeCompletionDate = _ref.rangeCompletionDate,
    isFocusable = _ref.isFocusable,
    dateAs = _ref.dateAs,
    calendarFrom = _ref.calendarFrom,
    calendarTo = _ref.calendarTo,
    navClassName = _ref.navClassName,
    tableClassName = _ref.tableClassName,
    dateClassName = _ref.dateClassName,
    dueDate = _ref.dueDate,
    formatToParts = _ref.formatToParts,
    _ref$formatterSkeleto = _ref.formatterSkeleton,
    formatterSkeleton = _ref$formatterSkeleto === void 0 ? 'yyyyMd' : _ref$formatterSkeleto,
    _ref$legendSelectedDa = _ref.legendSelectedDateLabel,
    legendSelectedDateLabel = _ref$legendSelectedDa === void 0 ? 'Selected' : _ref$legendSelectedDa,
    _ref$legendDueDateLab = _ref.legendDueDateLabel,
    legendDueDateLabel = _ref$legendDueDateLab === void 0 ? 'Due date' : _ref$legendDueDateLab,
    _ref$legendTodayLabel = _ref.legendTodayLabel,
    legendTodayLabel = _ref$legendTodayLabel === void 0 ? 'Today' : _ref$legendTodayLabel,
    isDateDisabled = _ref.isDateDisabled,
    onSelection = _ref.onSelection,
    onMonthChange = _ref.onMonthChange;
  /* istanbul ignore next */
  if (!onSelection) {
    onSelection = function () {
      return devWarning('Required prop `onSelection` is missing');
    };
  }
  var _currentDate = selectedDate || new Date();
  var _useState = useState(new Date(_currentDate.getFullYear(), _currentDate.getMonth())),
    _useState2 = _slicedToArray(_useState, 2),
    currentDate = _useState2[0],
    setCurrentDate = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isRTL = _useState4[0],
    setRTL = _useState4[1];
  var intlContext = useContext(IntlContext);
  var containerRef = useCallback(function (node) {
    if (node !== null) {
      var computedStyle = window.getComputedStyle(node);
      setRTL(computedStyle && computedStyle.direction === 'rtl');
    }
  }, []);
  var _firstDayOfWeek = useMemo(function () {
    return getFirstDayOfWeek(intlContext, firstDayOfWeek);
  }, [firstDayOfWeek]);
  var _useMemo = useMemo(function () {
      return getNames(intlContext, monthNames, dayNames);
    }, [monthNames, dayNames]),
    _monthNames = _useMemo._monthNames,
    _dayNames = _useMemo._dayNames;
  var _formatToParts = useMemo(function () {
    return getFormatter(intlContext, formatToParts, formatterSkeleton);
  }, [formatToParts, formatterSkeleton]);
  var tabIndex = !isFocusable ? -1 : 0;
  useEffect(function () {
    setCurrentDate(new Date(_currentDate.getFullYear(), _currentDate.getMonth()));
  }, [selectedDate]);
  var renderDateButton = function (dateInfo) {
    var isDisabled = dateInfo.isDisabled,
      isToday = dateInfo.isToday,
      isSelected = dateInfo.isSelected,
      isRangeStart = dateInfo.isRangeStart,
      isRangeEnd = dateInfo.isRangeEnd,
      isDue = dateInfo.isDue,
      inCurrentMonth = dateInfo.inCurrentMonth,
      inRange = dateInfo.inRange,
      date = dateInfo.date;
    var shouldApplySelectedStyles = !isDisabled && (isSelected || isRangeStart || isRangeEnd);
    var dateButtonClassNames = cx(styles.calendarDate, !inCurrentMonth && styles.calendarDateNotCurrent, isDisabled && styles.calendarDateDisabled, isToday && styles.calendarDateToday, isDue && styles.calendarDueDate, inRange && styles.calendarDateInRange, shouldApplySelectedStyles && styles.calendarDateSelected, shouldApplySelectedStyles && isDue && styles.calendarDueDateSelected);
    var props = {
      className: cx(dateButtonClassNames, dateClassName),
      onMouseDown: function onMouseDown(evt) {
        return evt.preventDefault();
      },
      onClick: function onClick() {
        return !(isDisabled || isSelected) && onSelection(date);
      },
      disabled: isDisabled,
      tabIndex: tabIndex,
      type: 'button'
    };
    if (dateAs) {
      props.dateInfo = dateInfo;
    }
    var formattedDate = _formatToParts(date);
    var button = /*#__PURE__*/React.createElement(dateAs || 'button', _extends({}, props, {
      "data-ppui": "true"
    }), formattedDate.day);
    if (isDue) {
      return /*#__PURE__*/React.createElement("div", {
        className: styles.calendarDueDateIconContainer,
        "data-ppui": "true"
      }, button, /*#__PURE__*/React.createElement(ClockSmIcon, {
        size: "xs",
        className: styles.calendarDueDateIcon,
        "data-ppui": "true"
      }));
    }
    return button;
  };
  var currentMonth = _monthNames[currentDate.getMonth()];
  var daysOfWeek = getDaysOfWeek(_firstDayOfWeek);
  var header = /*#__PURE__*/React.createElement("thead", {
    "data-ppui": "true"
  }, /*#__PURE__*/React.createElement("tr", {
    "data-ppui": "true"
  }, daysOfWeek.map(function (name) {
    return /*#__PURE__*/React.createElement("th", {
      key: name,
      "data-ppui": "true"
    }, /*#__PURE__*/React.createElement(CaptionText, {
      "data-ppui": "true"
    }, _dayNames[name]));
  })));
  var isPrevHidden = calendarFrom && areMonthsEqual(currentDate, calendarFrom);
  var isNextHidden = calendarTo && areMonthsEqual(currentDate, calendarTo);
  var calendarRange = '';
  if (isPrevHidden) {
    calendarRange = CalendarRange.START;
  }
  if (isNextHidden) {
    calendarRange = CalendarRange.END;
  }
  var dates = getDatesForMonth(currentDate, daysOfWeek, _firstDayOfWeek, selectedDate, range, rangeCompletionDate, dueDate, isDateDisabled, calendarRange);
  var dateRows = [];
  var shouldApplyRangeStyles = true;
  var isDueDateVisible = false;
  if (isDateDisabled) {
    var isSelectedDateDisabled = selectedDate && isDateDisabled(selectedDate);
    var isRangeCompletionDateDisabled = rangeCompletionDate && isDateDisabled(rangeCompletionDate);

    /* istanbul ignore next */
    if (isSelectedDateDisabled || isRangeCompletionDateDisabled) {
      shouldApplyRangeStyles = false;
    }
  }
  for (var i = 0; i < dates.length; i = i + 7) {
    var dateCols = [];
    for (var j = 0; j < 7; j++) {
      var _dates = dates[i + j],
        isRangeStart = _dates.isRangeStart,
        isRangeEnd = _dates.isRangeEnd,
        inRange = _dates.inRange,
        isDue = _dates.isDue;
      var allowInRange = inRange;

      // if start and end date is same don't need range styles
      // @ts-ignore
      if (isRangeStart && isRangeEnd) {
        allowInRange = false;
      }
      var dateColClassNames = cx(shouldApplyRangeStyles && allowInRange && styles.calendarCellInRange, shouldApplyRangeStyles && allowInRange && isRangeStart && styles.calendarCellRangeStart, shouldApplyRangeStyles && allowInRange && isRangeEnd && styles.calendarCellRangeEnd);
      if (!isDueDateVisible && isDue) {
        isDueDateVisible = true;
      }
      dateCols.push( /*#__PURE__*/React.createElement("td", {
        key: j,
        "data-ppui": "true"
      }, /*#__PURE__*/React.createElement("div", {
        className: dateColClassNames,
        "data-ppui": "true"
      }, renderDateButton(dates[i + j]))));
    }
    dateRows.push( /*#__PURE__*/React.createElement("tr", {
      key: i,
      "data-ppui": "true"
    }, dateCols));
  }
  var body = /*#__PURE__*/React.createElement("tbody", {
    "data-ppui": "true"
  }, dateRows);
  var formattedCurrentDate = _formatToParts(currentDate);
  var legend = /*#__PURE__*/React.createElement("div", {
    className: styles.legend,
    "data-ppui": "true"
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.legendItem,
    "data-ppui": "true"
  }, /*#__PURE__*/React.createElement("span", {
    className: cx(styles.legendKey, styles.calendarDateSelected),
    "data-ppui": "true"
  }), /*#__PURE__*/React.createElement(CaptionText, {
    as: "span",
    className: styles.legendItemLabel,
    "data-ppui": "true"
  }, legendSelectedDateLabel)), isDueDateVisible && /*#__PURE__*/React.createElement("div", {
    className: styles.legendItem,
    "data-ppui": "true"
  }, /*#__PURE__*/React.createElement(ClockSmIcon, {
    size: "xs",
    className: styles.dueDateLegendKey,
    "data-ppui": "true"
  }), /*#__PURE__*/React.createElement(CaptionText, {
    as: "span",
    className: styles.legendItemLabel,
    "data-ppui": "true"
  }, legendDueDateLabel)), /*#__PURE__*/React.createElement("div", {
    className: styles.legendItem,
    "data-ppui": "true"
  }, /*#__PURE__*/React.createElement("span", {
    className: cx(styles.legendKey, styles.calendarDateToday),
    "data-ppui": "true"
  }), /*#__PURE__*/React.createElement(CaptionText, {
    as: "span",
    className: styles.legendItemLabel,
    "data-ppui": "true"
  }, legendTodayLabel)));

  /* istanbul ignore next */
  return /*#__PURE__*/React.createElement("div", {
    className: className,
    ref: containerRef,
    "data-ppui": "true"
  }, /*#__PURE__*/React.createElement("div", {
    className: cx(styles.calendarNav, navClassName),
    "data-ppui": "true"
  }, /*#__PURE__*/React.createElement(IconButton, {
    svgIconComponent: isRTL ? ArrowRightIcon : ArrowLeftIcon,
    buttonType: "tertiary",
    onMouseDown: function onMouseDown(evt) {
      return evt.preventDefault();
    },
    onClick: function handlePrevClick() {
      currentDate.setMonth(currentDate.getMonth() - 1);
      var prevDate = new Date(currentDate.getFullYear(), currentDate.getMonth());
      setCurrentDate(prevDate);
      /* istanbul ignore next */
      if (onMonthChange) {
        onMonthChange(prevDate);
      }
    },
    className: cx(styles.calendarNavButtonPrev, !isFocusable && styles.calendarNavButtonsNotFocusable, isPrevHidden && styles.calendarNavButtonsHidden),
    tabIndex: tabIndex,
    "data-ppui": "true"
  }, prevMonthLabel), /*#__PURE__*/React.createElement(BodyText, {
    "data-ppui": "true"
  }, "".concat(currentMonth, " ").concat(formattedCurrentDate.year)), /*#__PURE__*/React.createElement(IconButton, {
    svgIconComponent: isRTL ? ArrowLeftIcon : ArrowRightIcon,
    buttonType: "tertiary",
    onMouseDown: function onMouseDown(evt) {
      return evt.preventDefault();
    },
    onClick: function handleNextClick() {
      currentDate.setMonth(currentDate.getMonth() + 1);
      var nextDate = new Date(currentDate.getFullYear(), currentDate.getMonth());
      setCurrentDate(nextDate);
      /* istanbul ignore next */
      if (onMonthChange) {
        onMonthChange(nextDate);
      }
    },
    className: cx(styles.calendarNavButtonNext, !isFocusable && styles.calendarNavButtonsNotFocusable, isNextHidden && styles.calendarNavButtonsHidden),
    tabIndex: tabIndex,
    "data-ppui": "true"
  }, nextMonthLabel)), /*#__PURE__*/React.createElement("table", {
    className: cx(styles.calendarTable, tableClassName),
    "data-ppui": "true"
  }, header, body), legend);
};

export { Calendar, DateInput };
